import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useStaticQuery, graphql } from 'gatsby'
import { BreakPoint, Styles } from 'src/components/shared/CssValues'
import { Typography } from 'src/components/atoms/Typography'
import ColorButton from 'src/components/atoms/ColorButton'
import { PcSpWidth } from 'src/components/shared/Constant'
import PardotForm from 'src/components/pardot/PardotForm'
import { css } from '@emotion/core'

type TopMainViewProps = {
  windowHeight: number | null
}

type BgImageProps = {
  src: string
  alt: string
}

const pardotFormCss = css`
  margin: 0;
  min-height: 432px;
  @media screen and (max-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    display: none;
  }
`

export const TopFirstView = () => {
  const [windowHeight, setWindowHeight] = useState(500)

  useEffect(() => {
    setWindowHeight(window.innerHeight)
  }, [])

  const data = useStaticQuery(graphql`
    query {
      topImagePcPng: file(relativePath: { eq: "top/img_main_pc.png" }) {
        publicURL
      }
      topImagePcWebp: file(relativePath: { eq: "top/img_main_pc.webp" }) {
        publicURL
      }
      topImageSpPng: file(relativePath: { eq: "top/img_main_sp.png" }) {
        publicURL
      }
      topImageSpWebp: file(relativePath: { eq: "top/img_main_sp.webp" }) {
        publicURL
      }
    }
  `)

  return (
    <Container>
      <FvContainer>
        <TopMainView windowHeight={windowHeight}>
          <TopContentsWrapper>
            <TopContents>
              <ImagePart>
                <Picture>
                  <source
                    media={`(max-width: ${PcSpWidth.SMARTPHONE_MAX_WIDTH - 1}px)`}
                    srcSet={data.topImageSpWebp.publicURL}
                    type="image/webp"
                  />
                  <source
                    media={`(min-width: ${PcSpWidth.SMARTPHONE_MAX_WIDTH}px)`}
                    srcSet={data.topImagePcWebp.publicURL}
                    type="image/webp"
                  />
                  <source
                    media={`(max-width: ${PcSpWidth.SMARTPHONE_MAX_WIDTH - 1}px)`}
                    srcSet={data.topImageSpPng.publicURL}
                  />
                  <source
                    media={`(min-width: ${PcSpWidth.SMARTPHONE_MAX_WIDTH}px)`}
                    srcSet={data.topImagePcPng.publicURL}
                  />
                  <BgImage
                    src={data.topImagePcPng.publicURL}
                    alt=""
                  />
                </Picture>
                <CatchPhrase>
                  <h2>
                    <div>コミュニティ成功の<br />パートナー</div>
                  </h2>
                  <CatchPhraseSub>
                    <p>コミューンはオンラインコミュニティの<br />企画・構築・運用を一気通貫でサポートします</p>
                  </CatchPhraseSub>
                  {typeof window !== 'undefined' && window.parent.screen.width < 1399 && window.parent.screen.width > 600 
                    && <ColorButton
                          text="３分でわかる資料ダウンロード"
                          to="/whitepapers/commmune_introduction"
                          size="medium"
                          arrow={false}
                        />
                  }
                </CatchPhrase>
                
              </ImagePart>
            </TopContents>
          </TopContentsWrapper>
          <PardotForm css={pardotFormCss} src='https://go.commmune.jp/l/915341/2022-07-25/hqnzp' height="100%" />
        </TopMainView>
        </FvContainer>
        <ButtonWrapper>
          {typeof window !== 'undefined' && window.parent.screen.width < 600
            && <ColorButton
              text="３分でわかる資料をメール送付"
              to="/whitepapers/commmune_introduction"
              size="medium"
              arrow={false}
            />
          }
        </ButtonWrapper>
    </Container>
    
  )
}

const Container = styled.div`
  padding-top: ${Styles.spacing[13]};
  @media screen and (max-width: ${BreakPoint.SMARTPHONE_MAX_WIDTH}) {
    padding-top: 0;
  }
`

const FvContainer = styled.div`
  background: ${Styles.color.primary[200]};
  @media screen and (max-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    min-height: 350px;
    height: calc(100vh - 150px);
  }
`

const TopMainView = styled.div<TopMainViewProps>`
  overflow: hidden;
  min-height: 600px;
  @media screen and (max-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    min-height: 350px;
    height: calc(100vh - 150px);
  }
  @media screen and (min-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    display: grid;
    grid-template-columns: 70% 30%;
  }
`

const TopContentsWrapper = styled.div`
  height: calc(100vh - 200px);
  @media screen and (min-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    height: calc(100vh - 150px);
  }
`

const TopContents = styled.div`
  align-items: center;
  height: 100%;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    height: auto;
  }
`
const CatchPhrase = styled(Typography)`
  color: ${Styles.color.primary[400]};
  font-weight: ${Styles.fontWeight.bold};
  line-height: ${Styles.lineHeight[200]};
  letter-spacing: ${Styles.letterSpacing[400]};
  text-align: center;
  font-size: min(8vw, ${Styles.fontSize[900]});
  z-index: 100;
  position: relative;
  text-align: left;
  padding: 40px 60px 0;
  @media screen and (min-width: ${BreakPoint.TABLET}) {
    margin: 0;
    font-size: min(4vw, ${Styles.fontSize[900]});
    top: 0;
    left: 0;
  }
  @media screen and (max-width: ${BreakPoint.TABLET}) {
    position: relative;
    padding: 20px 30px ;
  }
  @media screen and (max-width: ${BreakPoint.SMARTPHONE_MAX_WIDTH}) {
    padding-top: 125px;
  }
`
const CatchPhraseSub= styled(Typography)`
  font-size: min(8vw, ${Styles.fontSize[600]});
  line-height: ${Styles.lineHeight[300]};
  font-weight: ${Styles.fontWeight.bold};
  font-weight: ${Styles.fontWeight.semiBold};
  margin-top: ${Styles.spacing[5]};
  @media screen and (max-width: ${BreakPoint.TABLET}) {
    font-size: min(4vw, ${Styles.fontSize[400]});
    padding-bottom: 20px;
    margin-top: ${Styles.spacing[3]};
  }
`

const ImagePart = styled.div`
  @media screen and (min-width: ${BreakPoint.TABLET}) {
  }
  @media screen and (max-width: ${BreakPoint.TABLET}) {
  }
`

const Picture = styled.picture`
  position: absolute;
  object-fit: contain;
  width: 100%;
  @media screen and (min-width: ${BreakPoint.TABLET_MAX_WIDTH}) {
    width: 70%;
  }
`

const BgImage = styled.img<BgImageProps>`
  vertical-align: bottom;
  width: 100%;
  min-height: 500px;
  object-fit: cover;
  height: fit-content;
  height: calc(100vh - 150px);
`
const ButtonWrapper = styled.div`
  @media screen and (max-width: ${BreakPoint.MOBILE_L}) {
    padding: 30px;
    text-align: center;
  }
`